import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectRevenueImpactFilter } from 'app/pages/Home/slice/selectors';
import { MomentData, MilestonesGridData } from '../types';

/**
 * Custom hook to filter milestones grid data by Revenue Impact
 * Filters on:
 * - 'revenue-impact-yes' → shows moments where revenueImpact === true
 * - 'revenue-impact-no' → shows moments where revenueImpact === false
 * Filters on: moment.filters.revenueImpact (boolean)
 */
export const useFilterByRevenueImpact = (
  milestonesData: MilestonesGridData | null
): MilestonesGridData | null => {
  // Get selected revenue impact filters from Redux state
  const revenueImpactFilter = useSelector(selectRevenueImpactFilter);
  const selectedFilters = revenueImpactFilter?.selectedFilters || [];

  // Memoize the filtered data to avoid unnecessary recalculations
  const filteredData = useMemo(() => {
    // If no data, return null
    if (!milestonesData || !milestonesData.data) {
      return null;
    }

    // If no revenue impact filters are selected, return original data
    if (selectedFilters.length === 0) {
      return milestonesData;
    }

    // Check which filters are active
    const hasYesFilter = selectedFilters.includes('revenue-impact-yes');
    const hasNoFilter = selectedFilters.includes('revenue-impact-no');

    // If both yes and no are selected, return all data (no filtering needed)
    if (hasYesFilter && hasNoFilter) {
      return milestonesData;
    }

    // Filter the data based on selected revenue impact filters
    const filtered: MomentData[] = milestonesData.data.filter(moment => {
      const revenueImpactValue = moment.filters?.revenueImpact === true;

      // If 'revenue-impact-yes' is selected, show only moments with revenueImpact === true
      if (hasYesFilter) {
        return revenueImpactValue === true;
      }

      // If 'revenue-impact-no' is selected, show only moments with revenueImpact === false
      if (hasNoFilter) {
        return revenueImpactValue === false;
      }

      return false;
    });

    return {
      ...milestonesData,
      data: filtered
    };
  }, [milestonesData, selectedFilters]);

  return filteredData;
};
